import { useInfiniteQuery } from "@tanstack/react-query"
import { getPvpRouletteRoundHistory, getPaymentHistory } from "@/shared/api"
import { getUserIdFromToken } from "@/shared/api/auth"
import type { HistoryItem, PaymentHistoryItem } from "@/shared/api/types"

export const historyKeys = {
  all: ["history"] as const,
  infinite: () => [...historyKeys.all, "infinite"] as const,
}

export const paymentHistoryKeys = {
  all: ["paymentHistory"] as const,
  infinite: () => [...paymentHistoryKeys.all, "infinite"] as const,
}

export function useHistory(limit = 10) {
  return useInfiniteQuery({
    queryKey: historyKeys.infinite(),
    queryFn: async ({ pageParam }: { pageParam?: string }) => {
      const currentUserId = getUserIdFromToken()
      const response = await getPvpRouletteRoundHistory({
        cursor: pageParam,
        limit: Math.min(10, Math.max(1, limit)),
      })

      const items: HistoryItem[] = (response.items ?? []).map((item) => ({
        ...(function () {
          const winners = item.winner_party?.winners ?? []
          const me = currentUserId
            ? winners.find((w) => w.user_id === currentUserId)
            : undefined
          const isWin = Boolean(me)
          const netWinNano = me?.net_win_amount ?? 0
          return {
            result: isWin ? "win" : "lose",
            delta: String(netWinNano / 1e9),
          }
        })(),
        id: item.round_id,
        type: "pvp_roulette",
        currency: "TON",
        ts: undefined,
        meta: item as unknown as Record<string, unknown>,
      }))

      return { items, next_cursor: response.next_cursor ?? null }
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    staleTime: 1000 * 30,
  })
}

export function usePaymentHistory(limit = 20) {
  return useInfiniteQuery({
    queryKey: paymentHistoryKeys.infinite(),
    queryFn: async ({ pageParam }: { pageParam?: string }) => {
      const response = await getPaymentHistory({
        limit: Math.min(20, Math.max(1, limit)),
        cursor: pageParam,
      })
      // Hide half-finished operations, but include both TON and TELEGRAM_GIFT
      // currencies — the profile activity feed renders gift rows distinctly.
      const items: PaymentHistoryItem[] = (response.items ?? []).filter(
        (item) => item.status === "completed"
      )
      return { items, next_cursor: response.next_cursor ?? null }
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    staleTime: 1000 * 30,
  })
}
